import cloneDeep from 'lodash/cloneDeep'
import moment from 'moment-timezone'

import { splitTextIntoPosts } from '@/lib/scheduling'
import { cloneGallery } from '@/lib/utils'

import submitPlatformPost from '@/graphql/mutations/submissions/submitPlatformPost.gql'
import reschedulePlatformPost from '@/graphql/mutations/submissions/reschedulePlatformPost.gql'
import removeSubmissionFromPlatform from '@/graphql/mutations/submissions/removeSubmissionFromPlatform.gql'
import deletePlatformPost from '@/graphql/mutations/submissions/deletePlatformPost.gql'
import duplicatePlatformPost from '@/graphql/mutations/submissions/duplicatePlatformPost.gql'
import cancelPlatformAutoRepost from '@/graphql/mutations/submissions/cancelPlatformAutoRepost.gql'
import cancelPlatformAutoRemove from '@/graphql/mutations/submissions/cancelPlatformAutoRemove.gql'

import twitterPost from '@/graphql/queries/twitter/twitterPost.gql'
import allTwitterSubmissions from '@/graphql/queries/twitter/allTwitterSubmissions.gql'

export const MAX_TWEET_LENGTH = 280
export const VERIFIED_MAX_TWEET_LENGTH = 25000

export default {
  code: 'twitter',
  name: 'X / Twitter',
  icon: 'square-x-twitter',
  color: '#000000',
  accountNoun: 'Account',
  postNoun: 'Post',
  internalPostNoun: 'Tweet',
  repostNoun: 'Retweet',
  accountCapabilities: ['Submit posts.'],
  mutations: {
    // Dashboard
    submitPost: submitPlatformPost,
    submitPostVariables: (submission) => {
      return {
        postId: submission.postId,
        platform: submission.platform.toUpperCase()
      }
    },
    deletePost: deletePlatformPost,
    deletePostVariables: (submission) => {
      return {
        postId: submission.postId,
        platform: submission.platform.toUpperCase()
      }
    },
    reschedulePost: reschedulePlatformPost,
    reschedulePostVariables: (submission) => {
      return {
        postId: submission.postId,
        platform: submission.platform.toUpperCase()
      }
    },
    duplicatePost: duplicatePlatformPost,
    // History
    removeSubmissionFromPlatformMutation: removeSubmissionFromPlatform,
    cancelAutoRepostMutation: cancelPlatformAutoRepost,
    cancelAutoRemoveMutation: cancelPlatformAutoRemove
  },
  queries: {
    allSubmissions: allTwitterSubmissions,
    getPost: twitterPost
  },
  posts: {
    preparePostForScheduling: (post, publishingStatus = 'READY_TO_PUBLISH') => {
      let thread = []

      // Sync auto-remove
      const firstTweet = post.thread[0]
      if (!!firstTweet) {
        for (let tweet of post.thread) {
          tweet.removeAtAmount = firstTweet.removeAtAmount
          tweet.removeAtUnit = firstTweet.removeAtUnit
          tweet.removeMinLikes = firstTweet.removeMinLikes
        }
      }

      // Deep copy the thread so that we don't modify underlying gallery
      // objects, which breaks image previews.
      const formThread = cloneDeep(post.thread)

      for (let tweet of formThread) {
        let gallery = null
        if (tweet.gallery) {
          // We construct the gallery ourselves to remove fields like __typename
          gallery = {
            id: tweet.gallery.id,
            galleryMediaSet: []
          }
          gallery.galleryMediaSet = tweet.gallery.galleryMediaSet.map(
            (mediaSet) => {
              return {
                id: mediaSet.id,
                order: mediaSet.order,
                media: {
                  name: mediaSet.media.name,
                  altText: mediaSet.media.altText,
                  url: mediaSet.media.url,
                  externalId: mediaSet.media.externalId,
                  mimeType: mediaSet.media.mimeType,
                  size: mediaSet.media.size,
                  source: mediaSet.media.source
                }
              }
            }
          )
        }

        if (tweet.poll) {
          tweet.poll = {
            choices: tweet.poll.choices,
            durationMinutes: tweet.poll.durationMinutes
          }
        }

        thread.push({
          id: tweet.id,
          text: tweet.text,
          gallery: gallery,
          poll: tweet.poll,
          gifUrl: tweet.gifUrl,
          order: tweet.order,
          repostAtAmount: tweet.repostAtAmount,
          repostAtUnit: tweet.repostAtUnit,
          repostRepeatDays: tweet.repostRepeatDays,
          repostFromSocialAccount: getRepostFromSocialAccount(tweet),
          removeAtAmount: tweet.removeAtAmount,
          removeAtUnit: tweet.removeAtUnit,
          removeMinLikes: tweet.removeMinLikes,
          final: tweet.final
        })
      }

      return {
        id: post.id,
        username: post.username,
        replySettings: post.replySettings,
        thread: thread,
        postAt: post.postAt,
        publishingStatus
      }
    },
    createPost(socialAccount) {
      let post = {
        id: null,
        username: socialAccount.username,
        thread: [],
        replySettings: 'EVERYONE',
        postAt: moment().add(1, 'hour').startOf('hour').toDate()
      }

      post.thread.push(newTweet(post, 0))

      if (socialAccount.postSettings.autoRemove) {
        const autoRemove = socialAccount.postSettings.autoRemove
        post.thread[0].removeAtAmount = autoRemove.removeAtAmount
        post.thread[0].removeAtUnit = autoRemove.removeAtUnit
        post.thread[0].removeMinLikes = autoRemove.removeMinLikes
      }

      if (socialAccount.postSettings.autoRepost) {
        const autoRepost = socialAccount.postSettings.autoRepost
        post.thread[0].repostAtAmount = autoRepost.repostAtAmount
        post.thread[0].repostAtUnit = autoRepost.repostAtUnit
        post.thread[0].repostRepeatDays = autoRepost.repostRepeatDays
        if (autoRepost.repostFromSocialAccount) {
          post.thread[0].repostFromSocialAccount = {
            username: autoRepost.repostFromSocialAccount,
            platform: 'TWITTER'
          }
        }
      }

      if (
        socialAccount.postSettings.finalTweet &&
        socialAccount.postSettings.finalTweet.text
      ) {
        let finalTweet = newTweet(post, 1, true)
        finalTweet.text = socialAccount.postSettings.finalTweet.text
        finalTweet.final = true
        post.thread.push(finalTweet)
      }

      return post
    },
    getPost(post) {
      let thread = post.thread || post.submissions
      let firstPost = thread[0]
      return {
        caption: '',
        text: firstPost.text,
        gallery: cloneGallery(firstPost.gallery),
        postAt: moment(firstPost.postAt).toDate(),
        thread: thread.map((s) => {
          return {
            text: s.text,
            gallery: cloneGallery(s.gallery),
            final: s.final
          }
        })
      }
    },
    syncField(post, field, value, socialAccount) {
      if (field === 'caption' || field === 'text') {
        if (value.length <= MAX_TWEET_LENGTH) {
          if (post.thread.length > 0) {
            post.thread[0].text = value
          }
        } else {
          const paragraphs = splitTextIntoPosts(value, MAX_TWEET_LENGTH)
          paragraphs.forEach((paragraph, index) => {
            let hasTweetAtIndex = post.thread.length > index
            if (hasTweetAtIndex) {
              post.thread[index].text = paragraph
            } else {
              post.thread.push({
                ...newTweet(post, post.thread.length),
                text: paragraph
              })
            }
          })
          if (paragraphs.length < post.thread.length) {
            post.thread = post.thread.slice(0, paragraphs.length)
          }
        }
      } else if (field === 'gallery') {
        if (post.thread.length > 0) {
          post.thread[0].gallery = cloneDeep(value)
          post.thread[0].gifUrl = ''
        }
      } else if (field === 'postAt') {
        post.postAt = value
      } else if (field === 'thread') {
        // Extract the final tweet from the thread to add it afterwards
        const targetThread = post.thread.filter((t) => !t.final)
        const targetFinalTweet = post.thread.find((t) => t.final)

        // Sync the full thread to post.thread (except final tweets),
        // creating new tweets as needed.
        value.forEach((s, index) => {
          if (s.final) {
            return
          }
          let hasTweetAtIndex = targetThread.length > index
          if (hasTweetAtIndex) {
            // Replace existing tweet
            targetThread[index].text = s.text
            targetThread[index].gallery = cloneDeep(s.gallery)
          } else {
            // Add new tweet
            let tweet = newTweet(post, targetThread.length)
            tweet.text = s.text
            tweet.gallery = s.gallery
            tweet.gifUrl = ''
            targetThread.push(tweet)
          }
        })
        if (value.length < targetThread.length) {
          targetThread = targetThread.slice(0, value.length)
        }

        // Finally add the final tweet back to the thread.
        if (targetFinalTweet) {
          post.thread = [
            ...targetThread,
            { ...targetFinalTweet, order: targetThread.length }
          ]
        } else {
          post.thread = targetThread
        }
      }
      return post
    }
  },
  analytics: {
    metrics: [
      {
        metric: 'likes',
        label: 'Likes',
        icon: 'heart',
        aggregation: 'sum'
      },
      {
        metric: 'likes',
        label: 'Avg. Likes',
        aggregation: 'avg'
      },
      {
        metric: 'replies',
        label: 'Replies',
        icon: 'comment',
        aggregation: 'sum'
      },
      {
        metric: 'retweets',
        label: 'Retweets',
        icon: 'retweet',
        aggregation: 'sum'
      },
      {
        metric: 'quote_tweets',
        label: 'Quote Tweets',
        icon: 'quote-left'
      },
      {
        metric: 'impressions',
        label: 'Views',
        icon: 'eye',
        aggregation: 'sum'
      },
      {
        metric: 'url_link_clicks',
        label: 'Link Clicks',
        icon: 'link'
      },
      {
        metric: 'user_profile_clicks',
        label: 'Profile Clicks',
        icon: 'user',
        aggregation: 'sum'
      },
      {
        metric: 'posts',
        label: 'Tweets',
        icon: 'paper-plane',
        aggregation: 'sum'
      }
    ]
  }
}

function newTweet(post, order = null, final = false) {
  return {
    id: null,
    order,
    text: '',
    gallery: null,
    gifUrl: '',
    poll: null,
    postAt: post.postAt,
    final
  }
}

function getRepostFromSocialAccount(tweet) {
  if (tweet.repostFromSocialAccount && tweet.repostFromSocialAccount.username) {
    // Get rid of the `__typename` property.
    return {
      username: tweet.repostFromSocialAccount.username,
      platform: tweet.repostFromSocialAccount.platform
    }
  }
  return null
}
